/**
 * Reasons a quote can't be viewed in full or ordered by a customer.
 * @readonly
 */
export const QuoteUnavailableReasons = Object.freeze({
  /** Part or quote doc is missing or failed validation. */
  MISSING: 'missing',
  /** Still under internal review, or revoked back to pending after going live. */
  PENDING: 'pending',
  NO_QUOTE: 'no_quote',
  EXPIRED: 'expired',
  /** Priced with an engine other than v3, which can no longer be ordered. */
  UNSUPPORTED_VERSION: 'unsupported_version',
});

/**
 * Customer-facing explanation for each {@link QuoteUnavailableReasons} value,
 * shared by UI labels and function error messages.
 * @readonly
 */
export const QuoteUnavailableMessages = Object.freeze({
  [QuoteUnavailableReasons.MISSING]: 'This quote could not be found.',
  [QuoteUnavailableReasons.PENDING]: 'This quote is under review.',
  [QuoteUnavailableReasons.NO_QUOTE]: 'This device needs design changes before it can be quoted.',
  [QuoteUnavailableReasons.EXPIRED]: 'This quote has expired.',
  [QuoteUnavailableReasons.UNSUPPORTED_VERSION]: 'This quote has expired.',
});

/**
 * @typedef {'missing' | 'pending' | 'no_quote' | 'expired' | 'unsupported_version'} QuoteUnavailableReason
 * @typedef {{ available: true, reason: null } | { available: false, reason: QuoteUnavailableReason }} QuoteAvailability
 */

/**
 * Decides whether a customer may see a quote's pricing and order it. This is
 * the single definition of "orderable" shared by the quote view page, the cart
 * and the add-to-cart / checkout functions.
 *
 * Accepts model instances, `InvalidDocData`, or raw doc data (it only reads
 * `validation`, `status`, `pricingVersion` and `expiresAt`). Reasons are
 * checked in priority order: `pending` wins over `expired` so an unreviewed
 * quote never shows as merely expired.
 *
 * @param {object} params
 * @param {{ validation?: string, expiresAt?: number } | null | undefined} params.part
 * @param {{ validation?: string, status?: string, pricingVersion?: string } | null | undefined} params.quote
 * @param {number} [params.now] Current time in ms; defaults to `Date.now()`, pass it in tests.
 * @returns {QuoteAvailability}
 */
export function getQuoteAvailability({ part, quote, now = Date.now() }) {
  const unavailable = (reason) => ({ available: false, reason });

  if (!part || !quote || part.validation === 'failed' || quote.validation === 'failed') {
    return unavailable(QuoteUnavailableReasons.MISSING);
  }
  if (quote.status === 'pending') return unavailable(QuoteUnavailableReasons.PENDING);
  if (quote.status === 'no_quote') return unavailable(QuoteUnavailableReasons.NO_QUOTE);
  if (now > (part.expiresAt ?? 0)) return unavailable(QuoteUnavailableReasons.EXPIRED);
  if (quote.pricingVersion !== 'v3') return unavailable(QuoteUnavailableReasons.UNSUPPORTED_VERSION);
  return { available: true, reason: null };
}
